import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api.js';
import { useAuth } from '../../context/AuthContext.jsx';
import './maintenance-tickets-diagnostics.scss';

const BLUE = { color: '#005293' };
const PAGE_SIZE = 4;
const PRIO_BADGE = { Critical: 'bg-danger', High: 'bg-warning text-dark', Medium: 'bg-info text-dark', Low: 'bg-secondary' };
const STATUS_BADGE = { Open: 'badge-danger-soft', InProgress: 'badge-warning-soft', Resolved: 'badge-success-soft' };

function ago(iso) {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)} mins ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hrs ago`;
  const d = Math.floor(s / 86400);
  return d === 1 ? 'Yesterday' : `${d} days ago`;
}

const EMPTY_FORM = { chargePointId: '', priority: 'Critical', title: '', description: '' };

export default function MaintenanceTicketsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canRemote = user?.role === 'noc' || user?.role === 'admin';
  const [summary, setSummary] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('alarm_code') || '');
  const [query, setQuery] = useState(search);
  const [priority, setPriority] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState('');
  const [modalError, setModalError] = useState('');
  const [busy, setBusy] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [stations, setStations] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');

  const flash = (ok, msg) => {
    setToast({ ok, msg });
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const loadSummary = useCallback(() => {
    api.getMaintenanceSummary().then(setSummary).catch((e) => setError(e.message));
  }, []);

  const loadTickets = useCallback(() => {
    api.listMaintenanceTickets({
      page, pageSize: PAGE_SIZE, q: query,
      priority: priority === 'ALL' ? '' : priority, status: status === 'ALL' ? '' : status,
    }).then((r) => { setTickets(r.data); setTotal(r.meta.total); setError(''); }).catch((e) => setError(e.message));
  }, [page, query, priority, status]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadTickets(); }, [loadTickets]);

  const refresh = () => { loadSummary(); loadTickets(); };

  const inspect = async (id) => {
    setModalError('');
    try {
      const d = await api.getMaintenanceTicket(id);
      setDetail(d);
      setNotes(d.resolutionNotes || '');
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleStep = async (step) => {
    if (!detail || detail.status !== 'InProgress') return;
    const checklist = detail.checklist.map((c) => (c.id === step.id ? { ...c, done: !c.done } : c));
    setDetail({ ...detail, checklist });
    try {
      await api.saveMaintenanceDraft(detail.id, { checklist: checklist.map(({ id, done }) => ({ id, done })) });
    } catch (e) {
      setModalError(e.message);
    }
  };

  const markInProgress = async () => {
    setBusy(true);
    setModalError('');
    try {
      await api.startMaintenanceTicket(detail.id);
      setDetail(null);
      flash(true, `Work order ${detail.ticketNumber} is now In Progress.`);
      refresh();
    } catch (e) {
      setModalError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const resolve = async () => {
    if (!notes.trim()) {
      setModalError('Please enter technician resolution notes before signing off.');
      return;
    }
    setBusy(true);
    setModalError('');
    try {
      await api.resolveMaintenanceTicket(detail.id, {
        resolutionNotes: notes.trim(),
        checklist: detail.checklist.map(({ id, done }) => ({ id, done })),
      });
      setDetail(null);
      flash(true, `Work order ${detail.ticketNumber} successfully resolved and archived.`);
      refresh();
    } catch (e) {
      setModalError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const openCreate = async () => {
    setForm(EMPTY_FORM);
    setFormError('');
    setShowCreate(true);
    try {
      const list = await api.listMaintenanceStations();
      setStations(list);
      setForm((f) => ({ ...f, chargePointId: list[0]?.id || '' }));
    } catch (e) {
      setFormError(e.message);
    }
  };

  const saveNew = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.chargePointId) {
      setFormError('Please provide title and description for the new work order.');
      return;
    }
    setBusy(true);
    try {
      const t = await api.createMaintenanceTicket({ ...form, title: form.title.trim(), description: form.description.trim() });
      setShowCreate(false);
      flash(true, `Work order ${t.ticketNumber} dispatched.`);
      setPage(1);
      refresh();
    } catch (e) {
      setFormError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="container-fluid py-4" id="maintenance-tickets-container">
      {toast && <div className={`toast-lite alert ${toast.ok ? 'alert-success' : 'alert-danger'} shadow`} role="status" id="maintenance-toast">{toast.msg}</div>}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1" style={BLUE}>
            <i className="fas fa-tools me-2" style={BLUE} />Maintenance Tickets &amp; Diagnostics
          </h4>
          <p className="text-muted small mb-0">Dispatch, track, and resolve field service work orders for the charger network: hardware fault repairs and maintenance logs.</p>
        </div>
        <div className="mt-2 mt-md-0">
          <button className="btn btn-sm btn-primary" id="btn-new-work-order" onClick={openCreate}>
            <i className="fas fa-plus me-1" /> New Work Order
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert" id="maintenance-error">{error}</div>}

      <div className="row mb-4">
        <div className="col-12 col-md-3 mb-3">
          <div className="card border shadow-sm bg-white">
            <div className="card-body p-3">
              <div className="text-muted small text-uppercase fw-bold">Open Work Orders</div>
              <div className="d-flex justify-content-between align-items-baseline mt-2">
                <h3 className="fw-bold text-danger mb-0" id="stat-open">{summary ? summary.open : '—'}</h3>
                <span className="badge badge-danger-soft">Needs Dispatch</span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3 mb-3">
          <div className="card border shadow-sm bg-white">
            <div className="card-body p-3">
              <div className="text-muted small text-uppercase fw-bold">In Progress / On Site</div>
              <div className="d-flex justify-content-between align-items-baseline mt-2">
                <h3 className="fw-bold mb-0" style={{ color: '#d97706' }} id="stat-progress">{summary ? summary.inProgress : '—'}</h3>
                <span className="badge badge-warning-soft">Technicians Active</span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3 mb-3">
          <div className="card border shadow-sm bg-white">
            <div className="card-body p-3">
              <div className="text-muted small text-uppercase fw-bold">Resolved (Last 7 Days)</div>
              <div className="d-flex justify-content-between align-items-baseline mt-2">
                <h3 className="fw-bold text-success mb-0" id="stat-resolved">{summary ? summary.resolvedLast7Days : '—'}</h3>
                <span className="badge badge-success-soft">Closed</span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3 mb-3">
          <div className="card border shadow-sm bg-white">
            <div className="card-body p-3">
              <div className="text-muted small text-uppercase fw-bold">Mean Time to Repair</div>
              <div className="d-flex justify-content-between align-items-baseline mt-2">
                <h3 className="fw-bold mb-0" style={{ color: '#0284c7' }} id="stat-mttr">{summary?.meanTimeToRepairHours != null ? `${summary.meanTimeToRepairHours} hrs` : '—'}</h3>
                <span className="badge badge-info-soft">Resolved tickets</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border shadow-sm bg-white">
        <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center py-3">
          <h6 className="mb-0 fw-bold" style={BLUE}>
            <i className="fas fa-clipboard-list me-2" style={BLUE} />Active Hardware Work Orders
          </h6>
          <div className="d-flex flex-wrap align-items-center gap-2 mt-2 mt-md-0">
            <div className="input-group input-group-sm me-2" style={{ width: 260 }}>
              <span className="input-group-text bg-white"><i className="fas fa-search text-muted" /></span>
              <input type="text" className="form-control" id="search-tickets" placeholder="Search Ticket #, Station, Title..." aria-label="Search work orders"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select className="form-select form-select-sm me-2" id="filter-priority" style={{ width: 140 }} aria-label="Filter by priority"
              value={priority} onChange={(e) => { setPriority(e.target.value); setPage(1); }}>
              <option value="ALL">All Priorities</option>
              <option value="Critical">Critical</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
            </select>
            <select className="form-select form-select-sm" id="filter-status" style={{ width: 140 }} aria-label="Filter by status"
              value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="ALL">All Statuses</option>
              <option value="Open">Open</option>
              <option value="InProgress">In Progress</option>
              <option value="Resolved">Resolved</option>
            </select>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0" id="table-tickets">
              <thead>
                <tr className="text-muted small text-uppercase">
                  <th>Ticket #</th><th>Priority</th><th>Issue Title</th><th>Target Station</th><th>Assigned Tech</th><th>Status</th><th>Opened</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody id="tickets-table-body">
                {tickets.length === 0 && (
                  <tr><td colSpan={8} className="text-center p-4 text-muted">No maintenance tickets match the active search or filters.</td></tr>
                )}
                {tickets.map((t) => (
                  <tr key={t.id} className="ticket-row" data-ticket={t.ticketNumber}>
                    <td className="fw-bold font-monospace" style={BLUE}>{t.ticketNumber}</td>
                    <td><span className={`badge ${PRIO_BADGE[t.priority]} px-2 py-1`}>{t.priority}</span></td>
                    <td className="fw-bold text-dark">{t.title}</td>
                    <td><span className="badge bg-secondary">{t.ocppId}</span></td>
                    <td className="text-muted small">{t.assignedTech || 'Unassigned'}</td>
                    <td><span className={`badge ${STATUS_BADGE[t.status]} px-2 py-1`}>{t.status}</span></td>
                    <td className="small text-muted">{ago(t.openedAt)}</td>
                    <td className="text-end">
                      <button className="btn btn-sm btn-outline-primary me-1 btn-view-ticket" onClick={() => inspect(t.id)}>Inspect</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-footer bg-white d-flex flex-wrap justify-content-between align-items-center py-2 px-3">
          <span className="text-muted small">Showing <strong id="tickets-range-start">{rangeStart}</strong>–<strong id="tickets-range-end">{rangeEnd}</strong> of <strong id="tickets-count">{total}</strong> work orders</span>
          <nav aria-label="Tickets pagination">
            <ul className="pagination pagination-sm mb-0" id="tickets-pagination">
              {total > PAGE_SIZE && (
                <>
                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                    <button type="button" className="page-link" aria-label="Previous page" onClick={() => setPage(page - 1)}>&laquo;</button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                      <button type="button" className="page-link" onClick={() => setPage(p)}>{p}</button>
                    </li>
                  ))}
                  <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                    <button type="button" className="page-link" aria-label="Next page" onClick={() => setPage(page + 1)}>&raquo;</button>
                  </li>
                </>
              )}
            </ul>
          </nav>
        </div>
      </div>

      {detail && (
        <div className="modal-backdrop-lite" role="dialog" aria-modal="true" aria-labelledby="detail-ticket-number" id="modal-ticket-detail">
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content shadow-lg bg-white">
              <div className="modal-header">
                <h5 className="modal-title fw-bold" id="detail-ticket-number" style={BLUE}>
                  <i className="fas fa-wrench me-2" style={{ color: '#d97706' }} />{detail.ticketNumber}
                </h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setDetail(null)} />
              </div>
              <div className="modal-body p-4">
                <div className="row mb-3">
                  <div className="col-md-8">
                    <h5 className="fw-bold text-dark" id="detail-ticket-title">{detail.title}</h5>
                    <p className="text-muted small mb-0" id="detail-ticket-desc">{detail.description}</p>
                  </div>
                  <div className="col-md-4 text-md-end">
                    <span className={`badge ${PRIO_BADGE[detail.priority]} px-3 py-1 fw-bold`} id="detail-ticket-priority">{detail.priority}</span>
                    <div className="mt-2 text-muted small" id="detail-ticket-station">Station: {detail.ocppId}</div>
                  </div>
                </div>
                <div className="small text-muted mb-3" id="detail-ticket-location">
                  <div><i className="fas fa-map-marker-alt me-1" />{detail.station.site || '—'}{detail.station.address ? ` — ${detail.station.address}` : ''}</div>
                  <div><i className="fas fa-barcode me-1" />Serial {detail.station.serialNumber || '—'} · {[detail.station.vendor, detail.station.model].filter(Boolean).join(' ')} · Charger {detail.station.status}</div>
                  {detail.alarmHistory.length > 0 && (
                    <div className="mt-1" id="detail-alarm-history">
                      <i className="fas fa-bell me-1" />Alarm history: {detail.alarmHistory.map((a) => `${a.alarmCode} (${a.severity}, ${a.status})`).join('; ')}
                    </div>
                  )}
                </div>
                <div className="bg-light rounded p-3 mb-3 border">
                  <h6 className="fw-bold mb-2" style={BLUE}>
                    <i className="fas fa-check-square me-1 text-primary" />Technician Repair Protocol
                  </h6>
                  {detail.checklist.map((c, i) => (
                    <div className={`form-check ${i < detail.checklist.length - 1 ? 'mb-2' : ''}`} key={c.id}>
                      <input type="checkbox" className="form-check-input" id={`check-step-${c.id}`} checked={c.done}
                        disabled={detail.status !== 'InProgress'} onChange={() => toggleStep(c)} />
                      <label className="form-check-label text-dark small" htmlFor={`check-step-${c.id}`}>{c.label}</label>
                    </div>
                  ))}
                </div>
                <div className="mb-0">
                  <label className="form-label small fw-bold" htmlFor="input-resolution-notes">Technician Resolution Notes</label>
                  <textarea className={`form-control ${modalError && !notes.trim() ? 'is-invalid' : ''}`} rows={3} id="input-resolution-notes" maxLength={4000}
                    disabled={detail.status === 'Resolved'} value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Describe corrective repairs performed, parts replaced, and return-to-service validation..." />
                </div>
                {modalError && <div className="alert alert-danger mt-3 mb-0 py-2 small" role="alert" id="modal-ticket-error">{modalError}</div>}
                {detail.events.length > 0 && (
                  <div className="mt-3 small" id="detail-ticket-history">
                    <div className="fw-bold text-muted text-uppercase mb-1">Work order history</div>
                    <ul className="list-unstyled mb-0 text-muted">
                      {detail.events.slice(0, 6).map((e) => (
                        <li key={e.id}>{new Date(e.at).toLocaleString('en-PH')} · {e.actor || 'system'} · {e.detail}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-primary" id="btn-ticket-remote-control" disabled={!canRemote}
                  title={canRemote ? undefined : 'Remote commands are limited to NOC operators'}
                  onClick={() => navigate(`/charger-remote-controls?charger_id=${encodeURIComponent(detail.ocppId)}`)}>
                  <i className="fas fa-terminal me-1" /> Open Remote Controls
                </button>
                <button type="button" className="btn btn-warning fw-bold" id="btn-ticket-in-progress" onClick={markInProgress} disabled={busy || detail.status !== 'Open'}>
                  <i className="fas fa-play me-1" /> Mark In Progress
                </button>
                <button type="button" className="btn btn-success fw-bold" id="btn-ticket-resolve" onClick={resolve} disabled={busy || detail.status === 'Resolved'}>
                  {busy ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fas fa-check-double me-1" />} Sign-Off &amp; Resolve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="modal-backdrop-lite" role="dialog" aria-modal="true" aria-labelledby="create-ticket-title" id="modal-create-ticket">
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content shadow-lg bg-white">
              <div className="modal-header">
                <h5 className="modal-title fw-bold" id="create-ticket-title" style={BLUE}>
                  <i className="fas fa-plus me-2" style={BLUE} />Create Maintenance Work Order
                </h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowCreate(false)} />
              </div>
              <div className="modal-body p-4">
                <form id="form-create-ticket" onSubmit={(e) => e.preventDefault()}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label small fw-bold" htmlFor="new-t-station">Target Station</label>
                      <select className="form-select" id="new-t-station" value={form.chargePointId} onChange={(e) => setForm({ ...form, chargePointId: e.target.value })}>
                        {stations.map((s) => <option key={s.id} value={s.id}>{s.ocppId}{s.vendor ? ` (${s.vendor} ${s.model || ''})` : ''}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label small fw-bold" htmlFor="new-t-priority">Priority Level</label>
                      <select className="form-select" id="new-t-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                        <option value="Critical">Critical (Immediate dispatch)</option>
                        <option value="High">High (Within 4 hrs)</option>
                        <option value="Medium">Medium (Within 24 hrs)</option>
                        <option value="Low">Low (Routine maintenance)</option>
                      </select>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold" htmlFor="new-t-title">Issue Summary Title</label>
                    <input type="text" className="form-control" id="new-t-title" required maxLength={160} value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Broken Connector Latch on Gun #2" />
                  </div>
                  <div className="mb-0">
                    <label className="form-label small fw-bold" htmlFor="new-t-desc">Detailed Symptom Description</label>
                    <textarea className="form-control" id="new-t-desc" rows={3} required maxLength={2000} value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Describe observed failure, physical damage, or diagnostic fault details..." />
                  </div>
                  {formError && <div className="alert alert-danger mt-3 mb-0 py-2 small" role="alert" id="create-ticket-error">{formError}</div>}
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" id="btn-save-new-ticket" onClick={saveNew} disabled={busy}>Dispatch Ticket</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
